<template>
  <div class="tw:relative tw:mb-4 tw:text-white">
    <div
      class="tw:flex tw:flex-col tw:bg-[#1B3945] tw:border tw:border-[#919FB9] tw:rounded-xl tw:outline-[#B6B6B6] tw:outline-2 tw:min-w-24 tw:max-w-32 tw:min-h-40 card-bg"
    >
      <div class="tw:min-h-24">
        <img :src="imageSrc" alt="token image" />
      </div>
      <p class="tw:bg-[#244856] tw:pt-2 tw:pb-6 tw:font-bold tw:text-center tw:truncate tw:leading-4 name-bg">
        {{ displayName }}
      </p>
    </div>
    <span
      class="tw:inline-flex tw:bottom-0 tw:left-1/2 tw:absolute tw:justify-center tw:items-center tw:px-1.5 tw:border tw:border-[#919FB9] tw:rounded-xl tw:outline-[#B6B6B6] tw:outline-2 tw:min-w-12 tw:h-6 tw:text-center tw:leading-none tw:-translate-x-1/2 tw:translate-y-1/2 amount-bg"
    >
      {{ displayAmount }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { getIcon } from '~/composables/useTokensConfig';
import tokens, { type TokenIconUniquenessType } from '~/public/img/tokens';
import { tokenMetaData } from '~/utils';
import { type Tokens } from '~/utils/constants';

const { t } = useI18n();
const { blockchain } = useEnvs();

const {
  isNFT = false,
  hutIndex = 0,
  imageUrl,
  tokenKey,
  tokenAddress,
  name,
  amount
} = defineProps<{
  name?: string;
  tokenKey?: Tokens;
  amount: number | string;
  tokenAddress?: string;
  isNFT?: boolean;
  imageUrl?: string;
  hutIndex?: number;
}>();

const processTokenIcon = (icon: string | string[] | TokenIconUniquenessType, index: number) => {
  if (Array.isArray(icon)) return icon[index]?.toString() ?? '';
  if (typeof icon === 'object') return Object.values(icon)?.[index]?.toString() ?? '';
  return icon.toString();
};

const imageSrc = computed(() => {
  if (imageUrl) {
    return imageUrl;
  }
  if (tokenKey) {
    return tokens[tokenKey]?.toString() || '';
  }
  if (tokenAddress) {
    try {
      const tokenData = tokenMetaData(blockchain.contracts, tokenAddress);
      if (tokenData?.name?.contract) {
        const tokenIcon = tokens?.[tokenData.name.contract as Tokens];
        if (tokenIcon) {
          return processTokenIcon(tokenIcon, hutIndex);
        }
      }
      return getIcon(0, tokenAddress);
    } catch {
      const tokenData = tokenMetaData(blockchain.contracts, tokenAddress);
      const tokenIcon = tokens?.[tokenData?.name?.contract as Tokens];
      return tokenIcon ? processTokenIcon(tokenIcon, hutIndex) : '';
    }
  }
  return '';
});

const displayName = computed(() => {
  if (name) {
    return name;
  }
  if (tokenKey) {
    const tokenData = tokenMetaData(blockchain.contracts, blockchain.contracts[tokenKey].addresses.contract);
    return t(tokenData?.label || '', { count: 0 });
  }
  if (tokenAddress) {
    const tokenData = tokenMetaData(blockchain.contracts, tokenAddress);
    if (tokenData?.label) {
      return t(tokenData.label, { count: 0 });
    }
    if (isNFT) {
      return t('nft') || 'NFT';
    }
  }
  return '';
});

const isNftToken = computed(() => {
  if (isNFT) {
    return true;
  }
  if (tokenAddress) {
    const tokenData = tokenMetaData(blockchain.contracts, tokenAddress);
    return tokenData?.interface === 'erc721';
  }
  return false;
});

const displayAmount = computed(() => {
  if (isNftToken.value) {
    return 1;
  }
  return amount;
});
</script>

<style scoped>
.amount-bg {
  background: radial-gradient(85% 65% at 100% 100%, #1d3d2e 0%, #081b25 100%);
}

.name-bg {
  box-shadow: 0px 4px 5px 0px rgba(48, 114, 140, 0.5) inset;
}

.card-bg {
  box-shadow:
    0px 2px 0.2px 0.5px rgba(145, 159, 185, 1),
    0px 0px 3px 4px rgba(21, 23, 27, 1) inset,
    0px 0px 0px 3px rgba(104, 116, 138, 1) inset;
}
</style>
